import React, { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { EffectComposer, Bloom, Noise, Vignette } from "@react-three/postprocessing";
import { BlendFunction } from "postprocessing";
import * as THREE from "three";
import { latLonToVec3, greatCircleArc } from "@/lib/decisionGraphGeo";

// Dot-matrix geographic globe viewport for the Decision Graph. This REPLACES the
// node-graph geometry for both XIUS and GRIFFENDO while keeping every governance rule:
// point/arc colours still come from the shared legend (paintGraph/nodeHex) — gold
// normal, red registered stress, blue candidate, white verified-classical, cyan solver
// only with solver_identity+problem_id, green accepted only with a real Approval, gray
// unavailable. Land dots + sphere are the ambient accent from var(--qn-*) tokens.

const R = 3.2;
const VISIBLE_FROM = { source: 0, agent: 2, candidate: 3, decision: 4 };
const FOCUS_TYPE = ["source", "source", "agent", "candidate", "decision", "decision"];

function lerp(a, b, t) { return a + (b - a) * t; }

function nodeHex(node, legend, sem, state, dimmed) {
  if (dimmed) return legend.colors.gray;
  if (node.type === "source") return sem.hasStressEvent && state >= 1 ? legend.colors.red : legend.colors.gold;
  if (node.type === "agent") return legend.colors.gold;
  if (node.type === "candidate") return node.verifiedClassical ? legend.colors.white : legend.colors.blue;
  if (node.type === "decision") return sem.hasApproval && state >= 4 ? legend.colors.green : legend.colors.gray;
  return legend.colors.gray;
}

// Resolve var(--qn-*) tokens to concrete hex/rgb strings and re-read on theme change,
// so the dark cinematic / light analysis themes both drive the sphere with no hardcode.
function useCssVars(names) {
  const read = () => {
    const cs = getComputedStyle(document.documentElement);
    const out = {};
    names.forEach((n) => { out[n] = cs.getPropertyValue(n).trim() || "#888888"; });
    return out;
  };
  const [vars, setVars] = useState(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setVars(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "data-theme"] });
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names.join(",")]);
  return vars;
}

// Sample white-land pixels of an equirectangular mask into unit-sphere positions.
function sampleLandPoints(image, { lonSteps = 200, latSteps = 100, threshold = 110 } = {}) {
  const cvs = document.createElement("canvas");
  cvs.width = image.width; cvs.height = image.height;
  const ctx = cvs.getContext("2d");
  ctx.drawImage(image, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, cvs.width, cvs.height);
  const pts = [];
  for (let iy = 0; iy < latSteps; iy++) {
    const lat = 85 - (iy / (latSteps - 1)) * 155; // 85N .. -70S
    for (let ix = 0; ix < lonSteps; ix++) {
      const lon = -180 + (ix / lonSteps) * 360;
      const px = Math.min(width - 1, Math.floor(((lon + 180) / 360) * width));
      const py = Math.min(height - 1, Math.floor(((90 - lat) / 180) * height));
      const o = (py * width + px) * 4;
      if (data[o] > threshold && data[o + 1] > threshold && data[o + 2] > threshold) {
        const v = latLonToVec3(lat, lon, 1);
        pts.push(v[0], v[1], v[2]);
      }
    }
  }
  return new Float32Array(pts);
}

const DOT_VERT = `
  attribute vec3 aOffset; uniform float uScale; uniform float uR;
  varying vec2 vUv;
  void main(){
    vUv = uv;
    vec3 world = aOffset * uR;
    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    mv.xy += position.xy * uScale;
    gl_Position = projectionMatrix * mv;
  }`;
const DOT_FRAG = `
  uniform vec3 uColor; uniform float uOpacity;
  varying vec2 vUv;
  void main(){
    vec2 uv = vUv - 0.5; float d = length(uv);
    if (d > 0.5) discard;
    float a = smoothstep(0.5, 0.1, d);
    gl_FragColor = vec4(uColor, a * uOpacity);
  }`;

function LandDots({ accent, dimmed }) {
  const mat = useRef();
  const [positions, setPositions] = useState(null);
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { if (alive) setPositions(sampleLandPoints(img)); };
    img.src = `${process.env.PUBLIC_URL || ""}/earth-land.png`;
    return () => { alive = false; };
  }, []);
  const geo = useMemo(() => {
    if (!positions) return null;
    const quad = new THREE.PlaneGeometry(1, 1);
    const g = new THREE.InstancedBufferGeometry();
    g.index = quad.index;
    g.setAttribute("position", quad.attributes.position);
    g.setAttribute("uv", quad.attributes.uv);
    g.setAttribute("aOffset", new THREE.InstancedBufferAttribute(positions, 3));
    g.instanceCount = positions.length / 3;
    return g;
  }, [positions]);
  const uniforms = useMemo(() => ({
    uScale: { value: 0.028 }, uR: { value: R },
    uColor: { value: new THREE.Color(accent || "#2dd4bf") }, uOpacity: { value: 0.9 },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const col = useMemo(() => new THREE.Color(), []);
  useFrame(() => {
    if (!mat.current) return;
    col.set(accent || "#2dd4bf");
    mat.current.uniforms.uColor.value.lerp(col, 0.1);
    mat.current.uniforms.uOpacity.value = lerp(mat.current.uniforms.uOpacity.value, dimmed ? 0.35 : 0.85, 0.1);
  });
  if (!geo) return null;
  return (
    <mesh geometry={geo} frustumCulled={false} data-testid="globe-land-dots">
      <shaderMaterial ref={mat} vertexShader={DOT_VERT} fragmentShader={DOT_FRAG} uniforms={uniforms}
        transparent depthWrite={false} />
    </mesh>
  );
}

// A faint solid sphere so the dot-matrix reads as a globe (dark cinematic / light analysis).
function GlobeBody({ base }) {
  const mat = useRef();
  const col = useMemo(() => new THREE.Color(), []);
  useFrame(() => { if (mat.current) { col.set(base || "#05060c"); mat.current.color.lerp(col, 0.1); } });
  return (
    <mesh data-testid="globe-body">
      <sphereGeometry args={[R * 0.985, 48, 48]} />
      <meshBasicMaterial ref={mat} color={base || "#05060c"} transparent opacity={0.92} />
    </mesh>
  );
}

// Entity marker: a glowing point on the surface + a thin radial leader line + label.
function Marker({ node, geo, hex, active, dimmed, live, onSelect }) {
  const grpRef = useRef();
  const surf = useMemo(() => latLonToVec3(geo.lat, geo.lon, R), [geo.lat, geo.lon]);
  const outer = useMemo(() => latLonToVec3(geo.lat, geo.lon, R * 1.16), [geo.lat, geo.lon]);
  const dotMat = useRef();
  const lineMat = useRef();
  const col = useMemo(() => new THREE.Color(), []);
  const lineGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array([...surf, ...outer]), 3));
    return g;
  }, [surf, outer]);
  const scaleRef = useRef(0.0001);
  useFrame(() => {
    if (!grpRef.current) return;
    const target = active ? 1 : live ? 0.0001 : 0.4;
    scaleRef.current = lerp(scaleRef.current, target, 0.12);
    grpRef.current.scale.setScalar(scaleRef.current);
    grpRef.current.visible = scaleRef.current > 0.01;
    col.set(hex);
    if (dotMat.current) dotMat.current.color.lerp(col, 0.12);
    if (lineMat.current) { lineMat.current.color.lerp(col, 0.12); lineMat.current.opacity = lerp(lineMat.current.opacity, dimmed ? 0.15 : 0.55, 0.1); }
  });
  const clickable = node.type === "agent";
  return (
    <group ref={grpRef}>
      <mesh position={surf} frustumCulled={false}
        onClick={clickable ? (e) => { e.stopPropagation(); onSelect?.(node.id); } : undefined}
        onPointerOver={clickable ? () => { document.body.style.cursor = "pointer"; } : undefined}
        onPointerOut={clickable ? () => { document.body.style.cursor = "default"; } : undefined}
        data-testid={`globe-marker-${node.id}`}>
        <sphereGeometry args={[0.055, 12, 12]} />
        <meshBasicMaterial ref={dotMat} color={hex} toneMapped={false} />
      </mesh>
      <line geometry={lineGeo}>
        <lineBasicMaterial ref={lineMat} color={hex} transparent opacity={0.5} depthWrite={false} />
      </line>
      <Html position={outer} zIndexRange={[6, 0]} style={{ pointerEvents: "none" }}>
        <div className={`font-mono text-[9px] leading-tight whitespace-nowrap ${dimmed ? "opacity-30" : "opacity-90"}`}
          style={{ color: "var(--qn-foreground)", textShadow: "0 0 6px rgba(0,0,0,.9)" }}
          data-testid={`dg-tag-${node.id}`}>
          <div>{node.label}</div>
          <div className="text-[8px]" style={{ color: "var(--qn-foreground-subtle)" }}>{geo.name}</div>
        </div>
      </Html>
    </group>
  );
}

const ARC_VERT = `
  attribute float aT; uniform float uTime; uniform float uFlow; varying float vT; varying float vGlow;
  void main(){
    vT = aT;
    vGlow = 0.55 + 0.45 * sin((aT * 6.0 - uTime * 1.6) * 3.1416) * uFlow;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;
const ARC_FRAG = `
  uniform vec3 uColor; uniform float uOpacity; varying float vT; varying float vGlow;
  void main(){
    float ends = smoothstep(0.0, 0.06, vT) * smoothstep(1.0, 0.94, vT);
    gl_FragColor = vec4(uColor, uOpacity * vGlow * ends);
  }`;

function Arc({ from, to, hex, category, active, dimmed, reducedMotion }) {
  const matRef = useRef();
  const col = useMemo(() => new THREE.Color(), []);
  const { geo } = useMemo(() => {
    const pts = greatCircleArc(from, to, { r: R, lift: 0.32, segments: 48 });
    const pos = new Float32Array(pts.length * 3);
    const t = new Float32Array(pts.length);
    pts.forEach((p, i) => { pos[i * 3] = p[0]; pos[i * 3 + 1] = p[1]; pos[i * 3 + 2] = p[2]; t[i] = i / (pts.length - 1); });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("aT", new THREE.BufferAttribute(t, 1));
    return { geo: g };
  }, [from, to]);
  const uniforms = useMemo(() => ({
    uColor: { value: new THREE.Color(hex) }, uOpacity: { value: 0 }, uTime: { value: 0 }, uFlow: { value: reducedMotion ? 0 : 1 },
  }), [reducedMotion]); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((st) => {
    const u = matRef.current?.uniforms;
    if (!u) return;
    u.uTime.value = st.clock.elapsedTime;
    const target = dimmed ? 0.06 : active ? (category === "red" ? 0.85 : 0.6) : 0;
    u.uOpacity.value = lerp(u.uOpacity.value, target, 0.08);
    col.set(dimmed ? "#555c66" : hex);
    u.uColor.value.lerp(col, 0.08);
  });
  return (
    <line geometry={geo} data-testid="globe-arc">
      <shaderMaterial ref={matRef} vertexShader={ARC_VERT} fragmentShader={ARC_FRAG} uniforms={uniforms}
        transparent depthWrite={false} blending={THREE.AdditiveBlending} />
    </line>
  );
}

function ZoomReporter({ onZoom }) {
  const { camera } = useThree();
  const last = useRef(0);
  useFrame(() => {
    const d = camera.position.length();
    if (Math.abs(d - last.current) > 0.4) { last.current = d; onZoom?.(d); }
  });
  return null;
}

// Fixed camera work: slerp the view DIRECTION and lerp the radius on a sphere around
// the focus point (the post-camera-bug-fix pattern) — never a straight-line lerp.
function CameraRig({ focusWorld, controlsRef, interaction, reducedMotion }) {
  const { camera } = useThree();
  const dir = useMemo(() => new THREE.Vector3(), []);
  const desired = useMemo(() => new THREE.Vector3(), []);
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const tmpDir = useMemo(() => new THREE.Vector3(), []);
  const look = useMemo(() => new THREE.Vector3(), []);
  useFrame((st) => {
    const c = controlsRef.current;
    if (!c) return;
    if (interaction.current.dragging || performance.now() / 1000 - interaction.current.lastEnd < 5) return;
    look.copy(focusWorld);
    dir.copy(focusWorld);
    if (dir.length() < 0.4) dir.set(0.3, 0.2, 1);
    dir.y = Math.max(dir.y, 0.15);
    dir.normalize();
    const drift = reducedMotion ? 0 : 0.25 * Math.sin(st.clock.elapsedTime * 0.1);
    desired.copy(dir).multiplyScalar(8.4 + drift);
    const k = reducedMotion ? 1 : 0.02;
    const radius = lerp(camera.position.length(), desired.length(), k);
    tmp.copy(camera.position).normalize().lerp(tmpDir.copy(desired).normalize(), k).normalize();
    camera.position.copy(tmp).multiplyScalar(radius);
    c.target.lerp(look.multiplyScalar(0.15), k);
    c.update();
  });
  return null;
}

function Scene({ model, painted, state, legend, sem, reducedMotion, onSelectAgent, onZoom, live, dimmed, geo, controlsRef, interaction }) {
  const group = useRef();
  const vars = useCssVars(["--qn-primary", "--qn-background", "--qn-background-tertiary"]);
  useFrame((_, delta) => {
    if (group.current && !reducedMotion && !dimmed) group.current.rotation.y += delta * (live ? 0.045 : 0.06);
  });

  // Focus = centroid (on the sphere) of the placed nodes the CURRENT state is about.
  const focusWorld = useMemo(() => {
    const type = FOCUS_TYPE[state] || "source";
    const vecs = model.nodes.filter((n) => n.type === type && geo.placed.get(n.id))
      .map((n) => { const g = geo.placed.get(n.id); return latLonToVec3(g.lat, g.lon, 1); });
    if (!vecs.length) return new THREE.Vector3(0.3, 0.2, 1).normalize();
    const c = vecs.reduce((s, v) => [s[0] + v[0], s[1] + v[1], s[2] + v[2]], [0, 0, 0]);
    const v = new THREE.Vector3(c[0], c[1], c[2]);
    return v.lengthSq() < 1e-6 ? new THREE.Vector3(0.3, 0.2, 1).normalize() : v.normalize();
  }, [model, state, geo]);

  const nodeById = useMemo(() => new Map(model.nodes.map((n) => [n.id, n])), [model]);

  return (
    <>
      <color attach="background" args={[vars["--qn-background"] || "#05060c"]} />
      <fog attach="fog" args={[vars["--qn-background"] || "#05060c", 8, 24]} />
      <ambientLight intensity={0.8} />
      <ZoomReporter onZoom={onZoom} />
      <CameraRig focusWorld={focusWorld} controlsRef={controlsRef} interaction={interaction} reducedMotion={reducedMotion} />
      <group ref={group}>
        <GlobeBody base={vars["--qn-background-tertiary"] || "#0b1020"} />
        <LandDots accent={vars["--qn-primary"] || "#2dd4bf"} dimmed={dimmed} />
        {painted.map((l) => {
          const a = geo.placed.get(model.links[l.index]?.source);
          const b = geo.placed.get(model.links[l.index]?.target);
          if (!a || !b) return null;
          return (
            <Arc key={`arc-${l.index}`} from={a} to={b} hex={l.hex} category={l.category}
              active={l.category !== "gray" && !dimmed} dimmed={dimmed} reducedMotion={reducedMotion} />
          );
        })}
        {model.nodes.map((n) => {
          const g = geo.placed.get(n.id);
          if (!g) return null;
          const active = state >= (VISIBLE_FROM[n.type] ?? 0);
          return (
            <Marker key={n.id} node={n} geo={g} hex={nodeHex(nodeById.get(n.id), legend, sem, state, dimmed)}
              active={active} dimmed={dimmed} live={live} onSelect={onSelectAgent} />
          );
        })}
      </group>
      <EffectComposer multisampling={0}>
        <Bloom intensity={dimmed ? 0.7 : 1.8} luminanceThreshold={0.15} luminanceSmoothing={0.3} mipmapBlur radius={0.8} />
        <Noise opacity={0.1} premultiply blendFunction={BlendFunction.SOFT_LIGHT} />
        <Vignette eskil={false} offset={0.2} darkness={0.78} />
      </EffectComposer>
    </>
  );
}

export default function GlobeCanvas({ model, painted, state, legend, semantics, reducedMotion, onSelectAgent, onZoom, live, dimmed, geo }) {
  const controlsRef = useRef();
  const interaction = useRef({ dragging: false, lastEnd: -10 });
  useEffect(() => { document.body.style.cursor = "default"; }, []);
  const unplacedCount = geo?.unplaced?.length || 0;
  return (
    <div className="relative w-full h-full" data-testid="globe-viewport">
      <Canvas camera={{ position: [0, 1.5, 9], fov: 48 }} dpr={[1, 1.75]} gl={{ antialias: false, powerPreference: "high-performance" }}
        data-testid="decision-graph-webgl-canvas">
        <Scene model={model} painted={painted} state={state} legend={legend} sem={semantics}
          reducedMotion={reducedMotion} onSelectAgent={onSelectAgent} onZoom={onZoom}
          live={live} dimmed={dimmed} geo={geo} controlsRef={controlsRef} interaction={interaction} />
        <OrbitControls ref={controlsRef} enablePan={false} enableZoom autoRotate={false} minDistance={4.4} maxDistance={16}
          onStart={() => { interaction.current.dragging = true; }}
          onEnd={() => { interaction.current.dragging = false; interaction.current.lastEnd = performance.now() / 1000; }} />
      </Canvas>
      {unplacedCount > 0 && (
        <div className="absolute bottom-3 left-3 rounded-md bg-[var(--qn-glass)] backdrop-blur border border-[var(--qn-border)] px-3 py-1.5 text-[10px] font-mono text-[var(--qn-foreground-muted)]"
          data-testid="globe-unplaced-notice">
          {unplacedCount} {unplacedCount === 1 ? "entity" : "entities"} · location unavailable (no per-entity geo on this Run)
        </div>
      )}
    </div>
  );
}
